import React from "react";
import InnerHero from "../components/InnerHero";
import {
  Award,
  Briefcase,
  Building2,
  Calendar,
  CheckCircle2,
  GraduationCap,
  Mail,
  MapPin,
  Phone,
  Sparkles,
  TrendingUp,
  Users,
} from "lucide-react";

const Section = ({ id, title, icon: Icon, children, sub }) => (
  <section id={id} className="scroll-mt-32">
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 sm:gap-4 mb-5">
      <div className="min-w-0">
        <h3
          className="text-2xl md:text-3xl text-brand font-semibold flex items-start gap-2 leading-tight"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          {Icon && <Icon size={22} className="mt-1 flex-shrink-0" />} <span>{title}</span>
        </h3>
        {sub && <p className="text-sm text-[#3a3a3a]/80 italic mt-1">{sub}</p>}
      </div>
    </div>
    {children}
  </section>
);

const trainingModules = [
  ["Aptitude Training", "Students are supported through aptitude training, reasoning practice, and company-specific preparation."],
  ["Technical Training", "Technical skill development, coding practice, domain preparation, and industry certification guidance are provided."],
  ["Coding Practice", "Students are encouraged to improve problem-solving ability through coding practice and technical assessments."],
  ["Communication & Soft Skills", "Communication skill development, soft skills training, group discussion practice, and HR interview preparation are conducted."],
  ["Resume & Profile Building", "Students receive guidance for resume building, LinkedIn/profile building, application readiness, and documentation."],
  ["Mock Interviews", "Mock interviews, group discussions, and company-specific placement preparation help students face recruitment rounds confidently."],
  ["Career Counselling", "Students are guided for suitable job profiles, higher education, entrepreneurship, and career planning."],
  ["Internship Support", "Students are guided for industry internships, research internships, paid internships, virtual internships, and project-based internships."],
];

const missionPoints = [
  "To empower students to meet industry and societal challenges.",
  "To help undergraduate and postgraduate students through counselling, instructions, and training for the development of desired skills essential for suitable job profiles, higher education, and self-employment.",
  "To invite corporate industries, research or academic institutes, commercial organizations, and public sector undertakings for campus placement.",
  "To provide opportunities to all students to interact with corporate executives and prepare them with industry-required skills from time to time.",
];

const placementOfficer = {
  name: "Prof. Naveen S Hiremath",
  designation: "Training & Placement Officer",
  email: "placement@klsvdit.edu.in",
  image: "/FacultyCImages/naveen_hiremath_MECH.jpeg",
};

const placementTeam = [
  ["Prof. Seema R. Basarikatti", "srb@klsvdit.edu.in", "Civil Engineering"],
  ["Prof. Pranesh Kulkarni", "pkk@klsvdit.edu.in", "Computer Science and Engineering"],
  ["Prof. Narasimha Dixit", "nkd@klsvdit.edu.in", "Computer Science and Engineering (AI & ML)"],
  ["Prof. Pooja C Shindhe", "pcs@klsvdit.edu.in", "Electronics and Communication Engineering"],
  ["Prof. Naveen S Hiremath", "nsh@klsvdit.edu.in", "Mechanical Engineering"],
  ["Prof. Ravindra Motekar", "rsm@klsvdit.edu.in", "Electrical and Electronics Engineering"],
];

const placementProcess = [
  ["Job Requirement Sharing by Company", "The company shares the job description, number of vacancies, eligibility criteria, required skills, selection process, salary package, and other necessary details with the Training and Placement Cell."],
  ["Job Announcement", "The placement opportunity is communicated to eligible students along with job description, eligibility, salary details, selection rounds, and important instructions."],
  ["Student Registration", "Interested and eligible students register for the placement drive within the specified deadline."],
  ["Pre-Placement Talk", "The company interacts with students to explain its profile, work culture, job roles, growth opportunities, selection process, and expectations."],
  ["Aptitude / Technical Test", "Students appear for aptitude, reasoning, coding, domain-specific, or technical assessments as per the company's recruitment process."],
  ["Group Discussion", "Shortlisted students may participate in group discussion rounds to assess communication skills, confidence, teamwork, and analytical thinking."],
  ["Technical Interview", "Candidates are evaluated on subject knowledge, technical skills, problem-solving ability, project work, and role-specific competencies."],
  ["HR Interview", "The HR round assesses communication skills, attitude, career goals, adaptability, and overall suitability for the organization."],
  ["Final Selection", "The company announces the final list of selected candidates based on performance in all recruitment rounds."],
  ["Offer Letter Process", "Selected students receive offer letters through the company or the Training and Placement Cell, followed by joining formalities and further communication."],
];

const internshipHighlights = [
  "The Training and Placement Cell supports students in identifying and applying for suitable internship opportunities.",
  "Internships enhance practical knowledge, industry exposure, and career readiness.",
  "Students are guided for industry internships, research internships, paid internships, virtual internships, and project-based internships.",
  "The cell shares internship guidelines, eligibility details, application procedures, timelines, and required documentation.",
  "The cell collaborates with industry partners, organizations, and training providers to create meaningful internship opportunities.",
  "Students with strong internship performance may receive Pre-Placement Offer opportunities.",
];

const placementStats = [
  {
    year: "2025 - 2026",
    studentsPlaced: "160+",
    percentage: "60%",
    maxSalary: "5.5",
    avgSalary: "3.2",
  },
];

const Placements = () => {
  const dashboard = [
    ["Total Offers", "150+"],
    ["Students Placed", "160+"],
    ["Highest Package", "₹5.5 LPA"],
    ["Recruiters Visited", "15+"],
    ["Internship Offers", "400+"],
    ["Placement Percentage", "58.7%"],
  ];

  const navItems = [
    // ["overview", "Overview"],
    // ["featured-video", "Video"],
    // ["vision", "Vision"],
    // ["message", "Message"],
    // ["team", "Team"],
    // ["recruiters", "Recruiters"],
    // ["training", "Training"],
    // ["process", "Process"],
    // ["statistics", "Statistics"],
    // ["internships", "Internships"],
  ];

  return (
    <main>
      <InnerHero
        title="Training & Placement"
        image="/campus/placmenthero.jpg"
        breadcrumbs={[
          { label: "Home", to: "/" },
          { label: "Students", to: "/students" },
          { label: "Training & Placement" },
        ]}
      />

      <section className="bg-surface-alt/60 border-b border-brand/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-3 flex gap-4 sm:gap-x-6 overflow-x-auto text-xs font-sans-ui font-semibold tracking-wider text-brand">
          {navItems.map(([h, l]) => (
            <a key={h} href={`#${h}`} className="uppercase hover:underline whitespace-nowrap">
              {l}
            </a>
          ))}
        </div>
      </section>
      
      

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10 md:py-12">
        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-10 md:space-y-12 min-w-0">
            <Section id="overview" title="Training & Placement Overview" icon={Briefcase}>
              <span className="text-xs tracking-[0.2em] text-brand font-sans-ui font-semibold">
                CAREER DEVELOPMENT CELL
              </span>

              <p className="text-[#2a2a2a]/85 leading-relaxed text-[15.5px] mt-3">
                The Training and Placement Cell serves as a vital link between students and the industry. It works continuously to guide, train, and support students in building successful professional careers through structured training, internships, industry interaction, and campus recruitment opportunities.
              </p>

              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mt-5">
                {dashboard.map(([label, value]) => (
                  <div key={label} className="bg-[#7a1d2c] p-4 border border-[#7a1d2c]">
                    <div
                      className="text-3xl text-white font-semibold"
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      {value}
                    </div>
                    <div className="text-xs uppercase text-white/80 font-sans-ui tracking-widest mt-1">
                      {label}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-10">
                <Section id="vision" title="Placement Vision & Mission" icon={Award}>
                  <div className="grid md:grid-cols-2 gap-5">
                    <div className="bg-white border border-brand/15 p-6">
                      <h4 className="text-xl text-brand font-semibold mb-3">Our Vision</h4>
                      <p className="text-[15px] text-[#2a2a2a]/85 leading-relaxed">
                        To be the preferred choice of top corporate in India for campus recruitment by consistently updating and upgrading the competencies of students, matching the needs of the corporate sector thereby creating value for all the stakeholders.
                      </p>
                    </div>

                    <div className="bg-white border border-brand/15 p-6">
                      <h4 className="text-xl text-brand font-semibold mb-3">Our Mission</h4>
                      <ul className="space-y-3">
                        {missionPoints.map((point) => (
                          <li key={point} className="flex items-start gap-3">
                            <CheckCircle2 size={18} className="text-brand mt-0.5 flex-shrink-0" />
                            <span className="text-sm text-[#2a2a2a]/85 leading-relaxed">{point}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </Section>
              </div>

              <div className="grid md:grid-cols-3 gap-4 mt-5">
                <div className="bg-white border border-brand/15 p-5">
                  <h4 className="text-brand font-semibold mb-2">Role of the Placement Department</h4>
                  <p className="text-sm text-[#2a2a2a]/85 leading-relaxed">
                    The Placement Department coordinates training programs, internship opportunities, campus recruitment drives, industry visits, expert talks, and career development activities. It connects with recruiters and industries to create suitable career opportunities for students.
                  </p>
                </div>

                <div className="bg-white border border-brand/15 p-5">
                  <h4 className="text-brand font-semibold mb-2">Career Support Provided</h4>
                  <p className="text-sm text-[#2a2a2a]/85 leading-relaxed">
                    Students are supported through aptitude training, technical skill development, resume building, mock interviews, group discussions, soft skills training, career counselling, and company-specific placement preparation.
                  </p>
                </div>

                <div className="bg-white border border-brand/15 p-5">
                  <h4 className="text-brand font-semibold mb-2">Industry-Readiness Approach</h4>
                  <p className="text-sm text-[#2a2a2a]/85 leading-relaxed">
                    The Placement Cell prepares students as per current industry expectations by developing technical knowledge, communication skills, problem-solving ability, workplace attitude, and professional confidence.
                  </p>
                </div>
              </div>
            </Section>

            <Section id="featured-video" title="TPO Message" icon={Sparkles}>
              <div className="bg-white border border-brand/15 p-3 sm:p-5">
                <div className="w-full aspect-video bg-black rounded overflow-hidden">
                  <iframe
                    width="100%"
                    height="100%"
                    src="https://www.youtube.com/embed/eiPTzjr3tF8"
                    title="Training & Placement Video"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              </div>
            </Section>

            <Section id="message" title="Message from Placement Officer" icon={Users}>
              <div className="bg-white border border-brand/15 p-4 sm:p-7">
                <div className="bg-white border border-brand/10 p-4 sm:p-6 flex flex-col sm:flex-row items-start gap-5 sm:gap-6 mb-6">
                  <img
                    src={placementOfficer.image}
                    alt={`${placementOfficer.designation} ${placementOfficer.name}`}
                    className="w-36 h-44 sm:w-40 sm:h-48 object-cover border border-brand/10"
                  />
                  <div className="min-w-0">
                    <h4
                      className="text-2xl text-brand font-semibold"
                      style={{ fontFamily: "'Cormorant Garamond', serif" }}
                    >
                      {placementOfficer.name}
                    </h4>
                    <p className="mt-2 text-[#2a2a2a]/85">{placementOfficer.designation}</p>
                    <a
                      href={`mailto:${placementOfficer.email}`}
                      className="inline-flex items-start gap-2 mt-3 text-sm text-brand hover:underline break-all"
                    >
                      <Mail size={14} className="mt-0.5 flex-shrink-0" />
                      {placementOfficer.email}
                    </a>
                  </div>
                </div>

                <div className="border-t border-brand/10 pt-5">
                  <p className="leading-relaxed text-[#2a2a2a]/85">
                    The Training and Placement Cell is committed to empowering students with the right skills, confidence, and professional attitude required to succeed in today's competitive job market. Our focus is not only on placements, but also on developing employability, industry readiness, communication skills, technical competence, and a strong career mindset among students.
                  </p>

                  <p className="mt-4 leading-relaxed text-[#2a2a2a]/85">
                    We continuously work towards building strong industry connections and creating meaningful opportunities through training programs, internships, expert sessions, industrial visits, and campus recruitment drives. We invite recruiters and industry partners to collaborate with us in identifying young, talented, and motivated graduates who are ready to contribute effectively to the professional world.
                  </p>

                  <p className="mt-4 leading-relaxed text-[#2a2a2a]/85">
                    To our students, I encourage you to make the best use of every training opportunity, stay committed to continuous learning, and prepare yourself with confidence for the career ahead.
                  </p>
                </div>
              </div>
            </Section>

            <Section id="team" title="Placement Team" icon={Users}>
              <div className="grid gap-3 md:hidden">
                {placementTeam.map(([name, email, stream]) => (
                  <article key={`${name}-${email}-${stream}-card`} className="bg-white border border-brand/15 p-4">
                    <h4 className="text-brand font-semibold leading-snug">{name}</h4>
                    <a href={`mailto:${email}`} className="mt-1 block text-sm text-brand hover:underline break-all">
                      {email}
                    </a>
                    <p className="mt-2 text-sm text-[#2a2a2a]/85">{stream}</p>
                  </article>
                ))}
              </div>
              <div className="hidden md:block overflow-x-auto bg-white border border-brand/15">
                <table className="w-full border-collapse text-sm">
                  <thead className="bg-surface-alt/70">
                    <tr>
                      <th className="border border-brand/20 px-4 py-3 text-left">Name</th>
                      <th className="border border-brand/20 px-4 py-3 text-left">Email ID</th>
                      <th className="border border-brand/20 px-4 py-3 text-left">Stream / Role</th>
                    </tr>
                  </thead>
                  <tbody>
                    {placementTeam.map(([name, email, stream]) => (
                      <tr key={`${name}-${email}-${stream}`}>
                        <td className="border border-brand/20 px-4 py-3">{name}</td>
                        <td className="border border-brand/20 px-4 py-3">{email}</td>
                        <td className="border border-brand/20 px-4 py-3">{stream}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>

            <Section id="recruiters" title="Top Recruiters" icon={Building2}>
              <div className="overflow-hidden py-2 -mx-4 sm:-mx-6 lg:-mx-10">
                <div className="flex overflow-hidden">
                  <div className="marquee-track flex items-center gap-10 sm:gap-16" style={{ animationDuration: "90s" }}>
                    {[...Array(43)].concat([...Array(43)]).map((_, i) => (
                      <img
                        key={i}
                        src={`/recruters/${(i % 43) + 1}.png`}
                        alt={`Recruiter ${(i % 43) + 1}`}
                        className="max-h-20 w-auto object-contain flex-shrink-0"
                      />
                    ))}
                  </div>
                </div>
              </div>
            </Section>

            <Section
              id="training"
              title="Training & Career Development"
              icon={GraduationCap}
              sub="Structured training programmes and continuous guidance to build employability and industry readiness."
            >
              <div className="bg-white border border-brand/15 p-5 mb-5">
                <p className="text-[#2a2a2a]/85 leading-relaxed text-[15.5px]">
                  The Training and Placement Cell focuses on the overall career development of students by providing structured training programs and continuous guidance. These initiatives help students strengthen their aptitude, technical knowledge, communication skills, professional confidence, and industry readiness.
                </p>
                <p className="text-[#2a2a2a]/85 leading-relaxed text-[15.5px] mt-3">
                  Through these activities, the Placement Cell ensures that students are well-prepared to face recruitment processes and meet the expectations of today's competitive job market.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {trainingModules.map(([title, description]) => (
                  <div key={title} className="bg-white p-4 border border-brand/15">
                    <h4 className="text-brand font-semibold">{title}</h4>
                    <p className="text-sm text-[#2a2a2a]/85 mt-2 leading-relaxed">{description}</p>
                  </div>
                ))}
              </div>
            </Section>

            <Section
              id="process"
              title="Placement Process"
              icon={Sparkles}
              sub="A structured and transparent process for smooth coordination between recruiters and students."
            >
              <div className="bg-white border border-brand/15 p-5 mb-5">
                <p className="text-[#2a2a2a]/85 leading-relaxed text-[15.5px]">
                  The Training and Placement Cell follows a structured and transparent placement process to ensure smooth coordination between recruiters and students. The process is designed to prepare students effectively and support companies in identifying suitable talent.
                </p>
              </div>

              <ol className="space-y-2">
                {placementProcess.map(([title, description], index) => (
                  <li key={title} className="flex items-start gap-3 bg-white p-4 border border-brand/15">
                    <span className="w-7 h-7 flex items-center justify-center bg-brand text-surface text-sm font-semibold flex-shrink-0">
                      {index + 1}
                    </span>
                    <span>
                      <strong className="text-brand">{title}</strong>
                      <span className="block text-[15px] text-[#2a2a2a]/85 mt-1 leading-relaxed">
                        {description}
                      </span>
                    </span>
                  </li>
                ))}
              </ol>
            </Section>

            <Section id="statistics" title="Placement Statistics" icon={TrendingUp}>
              <div className="grid gap-3 md:hidden">
                {placementStats.map((stat) => (
                  <article key={stat.year} className="bg-white border border-brand/15 p-4">
                    <h4 className="text-brand font-semibold">{stat.year}</h4>
                    <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <dt className="text-[#2a2a2a]/65">Students Placed</dt>
                        <dd className="font-semibold text-[#2a2a2a]">{stat.studentsPlaced}</dd>
                      </div>
                      <div>
                        <dt className="text-[#2a2a2a]/65">% Placed</dt>
                        <dd className="font-semibold text-[#2a2a2a]">{stat.percentage}</dd>
                      </div>
                      <div>
                        <dt className="text-[#2a2a2a]/65">Max. Salary LPA</dt>
                        <dd className="font-semibold text-[#2a2a2a]">{stat.maxSalary}</dd>
                      </div>
                      <div>
                        <dt className="text-[#2a2a2a]/65">Avg. Salary LPA</dt>
                        <dd className="font-semibold text-[#2a2a2a]">{stat.avgSalary}</dd>
                      </div>
                    </dl>
                  </article>
                ))}
              </div>
              <div className="hidden md:block overflow-x-auto bg-white border border-brand/15">
                <table className="w-full border-collapse text-sm">
                  <thead className="bg-surface-alt/70">
                    <tr>
                      <th className="border border-brand/20 px-4 py-3 text-left">Year</th>
                      <th className="border border-brand/20 px-4 py-3 text-left">Number of Students Placed</th>
                      <th className="border border-brand/20 px-4 py-3 text-left">% Placed for Eligible Students</th>
                      <th className="border border-brand/20 px-4 py-3 text-left">Max. Salary LPA</th>
                      <th className="border border-brand/20 px-4 py-3 text-left">Avg. Salary LPA</th>
                    </tr>
                  </thead>
                  <tbody>
                    {placementStats.map((stat) => (
                      <tr key={stat.year}>
                        <td className="border border-brand/20 px-4 py-3">{stat.year}</td>
                        <td className="border border-brand/20 px-4 py-3">{stat.studentsPlaced}</td>
                        <td className="border border-brand/20 px-4 py-3">{stat.percentage}</td>
                        <td className="border border-brand/20 px-4 py-3">{stat.maxSalary}</td>
                        <td className="border border-brand/20 px-4 py-3">{stat.avgSalary}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>

            <Section id="internships" title="Internship Support" icon={Briefcase}>
              <div className="bg-white border border-brand/15 p-4 sm:p-7">
                <div className="space-y-4 text-[#2a2a2a]/85 leading-relaxed text-[15.5px]">
                  {internshipHighlights.map((item) => (
                    <p key={item}>{item}</p>
                  ))}
                </div>

                <div className="grid md:grid-cols-3 gap-4 mt-6">
                  {[
                    ["Stipend Based Internship", "130+"],
                    ["Free Internships", "100+"],
                    ["Total Students Undergone Internships", "400+"],
                  ].map(([label, value]) => (
                    <div key={label} className="bg-surface-alt/50 p-5 border border-brand/15">
                      <h4 className="text-brand font-semibold">{label}</h4>
                      <p
                        className="text-3xl mt-2 text-brand"
                        style={{ fontFamily: "'Cormorant Garamond', serif" }}
                      >
                        {value}
                      </p>
                    </div>
                  ))}
                </div>

                <div className="mt-6 bg-surface-alt/40 p-5 border border-brand/15">
                  <h4 className="text-brand font-semibold mb-3">Internship Domains</h4>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div className="bg-white border border-brand/10 p-4">
                      <p className="text-sm uppercase tracking-widest text-brand font-semibold">
                        IT Domains
                      </p>
                      <p
                        className="text-3xl text-brand mt-2"
                        style={{ fontFamily: "'Cormorant Garamond', serif" }}
                      >
                        60%
                      </p>
                    </div>
                    <div className="bg-white border border-brand/10 p-4">
                      <p className="text-sm uppercase tracking-widest text-brand font-semibold">
                        Non-IT Domains
                      </p>
                      <p
                        className="text-3xl text-brand mt-2"
                        style={{ fontFamily: "'Cormorant Garamond', serif" }}
                      >
                        40%
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </Section>
          </div>

          <aside className="space-y-5 min-w-0">
            <div className="bg-white border border-brand/15 p-5 lg:sticky lg:top-36 self-start">
              <h4 className="text-xs uppercase tracking-widest text-brand font-semibold font-sans-ui">
                Quick Facts
              </h4>

              <div className="mt-3 space-y-3 text-sm">
                <div className="flex items-start gap-2">
                  <TrendingUp size={14} className="text-brand flex-shrink-0 mt-0.5" />
                  <span>
                    Placement Percentage: <strong>58.7%</strong>
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <Award size={14} className="text-brand flex-shrink-0 mt-0.5" />
                  <span>
                    Highest Package: <strong>₹5.5 LPA</strong>
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <Briefcase size={14} className="text-brand flex-shrink-0 mt-0.5" />
                  <span>
                    Average Package: <strong>₹3.2 LPA</strong>
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <Users size={14} className="text-brand flex-shrink-0 mt-0.5" />
                  <span>
                    Students Placed: <strong>160+</strong>
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <Calendar size={14} className="text-brand flex-shrink-0 mt-0.5" />
                  <span>
                    Batch: <strong>2025-26</strong>
                  </span>
                </div>
              </div>

              <a
                href="#recruiters"
                className="block mt-5 text-center bg-brand text-surface py-2.5 text-sm font-semibold font-sans-ui hover:bg-brand-dark transition"
              >
                View Recruiters
              </a>

              <div className="mt-6 pt-5 border-t border-brand/10">
                <h4 className="text-xs uppercase tracking-widest text-brand font-semibold font-sans-ui mb-3">
                  Contact Placement Cell
                </h4>

                <ul className="space-y-2 text-sm text-[#2a2a2a]">
                  <li className="flex items-start gap-2">
                    <Users size={14} className="text-brand mt-0.5 flex-shrink-0" />
                    <span>
                      Placement Officer: <strong>Prof. Naveen S Hiremath</strong>
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Briefcase size={14} className="text-brand mt-0.5 flex-shrink-0" />
                    <span>Designation: Training & Placement Officer</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <MapPin size={14} className="text-brand mt-0.5 flex-shrink-0" />
                    <span>Office: Training & Placement Cell, KLS VDIT Haliyal</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Mail size={14} className="text-brand mt-0.5 flex-shrink-0" />
                    <span className="break-all">Email: placement@klsvdit.edu.in</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Phone size={14} className="text-brand mt-0.5 flex-shrink-0" />
                    <span>Phone: +91-9482294805</span>
                  </li>
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
};

export default Placements;
